// src/pages/AdminDriverDocumentsPage.js
// Admin page for company-wide documents that every signed-in driver sees on
// their portal landing page (insurance certificate, blank permits, SOPs,
// safety docs, forms). Backed by /api/driver-documents —
// see backend/routes/driver_documents.js.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../scripts/api";
import { FileText, Upload, RefreshCw, Trash2, Archive, RotateCcw } from "lucide-react";

const KINDS = [
  { v: "insurance",        label: "Insurance Certificate", icon: "🛡️" },
  { v: "permit_template",  label: "Permit / Template",      icon: "📄" },
  { v: "sop",              label: "SOP / Procedure",        icon: "📖" },
  { v: "safety",           label: "Safety",                 icon: "⚠️" },
  { v: "form",             label: "Form",                   icon: "📝" },
  { v: "other",            label: "Other",                  icon: "📎" },
];

function fmtBytes(n) {
  if (!Number.isFinite(+n) || +n <= 0) return "";
  const kb = +n / 1024; if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;  return `${mb.toFixed(1)} MB`;
}

export default function AdminDriverDocumentsPage() {
  const [rows, setRows]               = useState([]);
  const [busy, setBusy]               = useState(false);
  const [msg, setMsg]                 = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  const [kind, setKind]   = useState("insurance");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.get("/driver-documents", { params: includeInactive ? { all: "1" } : {} });
      setRows(Array.isArray(data?.documents) ? data.documents : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load documents.");
      setRows([]);
    } finally { setBusy(false); }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { setMsg("Pick a file to upload."); return; }
    setUploading(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      if (label.trim()) fd.append("label", label.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      await api.post("/driver-documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setLabel(""); setNotes("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Upload failed.");
    } finally { setUploading(false); }
  };

  const changeKind = async (row, newKind) => {
    try { await api.patch(`/driver-documents/${row.id}`, { kind: newKind }); await load(); }
    catch (e) { setMsg(e?.response?.data?.error || "Update failed."); }
  };

  const setActive = async (row, active) => {
    try { await api.patch(`/driver-documents/${row.id}`, { is_active: active ? 1 : 0 }); await load(); }
    catch (e) { setMsg(e?.response?.data?.error || "Update failed."); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.label || row.original_name}"? This removes the file permanently.`)) return;
    try { await api.delete(`/driver-documents/${row.id}`); await load(); }
    catch (e) { setMsg(e?.response?.data?.error || "Delete failed."); }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>
            <FileText size={22} style={{ verticalAlign: -4, marginRight: 8 }} />
            Driver Documents
          </h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Company-wide reference docs available to every signed-in driver on their portal
            — insurance certificate, blank permits, SOPs, safety handouts.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <label className="row" style={{ gap: 6, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)} />
            Show inactive
          </label>
          <button className="btn-outline" onClick={load} disabled={busy}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Upload form */}
      <div className="card" style={{ marginTop: 14, background: "#f8fafc" }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>
          <Upload size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
          Upload new document
        </div>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ maxWidth: 240 }}>
            {KINDS.map((k) => <option key={k.v} value={k.v}>{k.icon} {k.label}</option>)}
          </select>
          <input
            className="input" placeholder="Display label (optional — defaults to file name)"
            value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200}
            style={{ flex: "1 1 240px" }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ flex: "1 1 240px" }}
          />
        </div>
        <input
          className="input" placeholder="Notes (optional — shown under the document in the driver portal)"
          value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500}
          style={{ width: "100%", marginTop: 8 }}
        />
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn" onClick={upload} disabled={uploading || !file}>
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        {msg && <div style={{ marginTop: 8, color: "#dc2626", fontSize: 13 }}>{msg}</div>}
      </div>

      {/* List */}
      <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
        {busy && rows.length === 0 ? <div className="muted">Loading…</div> : null}
        {!busy && rows.length === 0 ? <div className="muted">No documents uploaded yet.</div> : null}
        {rows.map((r) => {
          const kindMeta = KINDS.find((k) => k.v === r.kind) || KINDS[KINDS.length - 1];
          return (
            <div key={r.id} className="card" style={{
              display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
              background: r.is_active ? "#fff" : "#f1f5f9",
              opacity: r.is_active ? 1 : 0.7,
            }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 700 }}>
                  {kindMeta.icon} <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "#1d4ed8" }}>
                    {r.label || r.original_name}
                  </a>
                  {!r.is_active && <span style={{ marginLeft: 8, fontSize: 11, color: "#475569", fontWeight: 600 }}>(inactive)</span>}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {r.original_name}
                  {r.size_bytes ? ` · ${fmtBytes(r.size_bytes)}` : ""}
                  {r.uploaded_at ? ` · uploaded ${String(r.uploaded_at).slice(0, 16).replace("T", " ")}` : ""}
                </div>
                {r.notes && <div style={{ fontSize: 12, marginTop: 2 }}>{r.notes}</div>}
              </div>
              <select
                className="input" value={r.kind} onChange={(e) => changeKind(r, e.target.value)}
                style={{ maxWidth: 200 }}
              >
                {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
              {r.is_active ? (
                <button className="btn-outline" onClick={() => setActive(r, false)} title="Hide from drivers">
                  <Archive size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                  Deactivate
                </button>
              ) : (
                <button className="btn-outline" onClick={() => setActive(r, true)} title="Re-activate for drivers">
                  <RotateCcw size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                  Re-activate
                </button>
              )}
              <button className="btn-outline" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => remove(r)}>
                <Trash2 size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                Delete
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
